import { useState } from 'react';
import { Box, Text, Button, Flex, Icon, Checkbox, Heading } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Alert, Completed, Delete } from '@vibe/icons';
import { deletePortalByPortalId, deletePortalStorage } from '../services/portal';
import { extractErrorMessage } from '../lib/utils';

type DeletePortalModalProps = {
  isOpen: boolean;
  portalName: string | null;
  boardName: string | null;
  portalId: string;
  boardId: string | null;
  onClose: (didDelete: boolean) => void;
};

type Status = 'idle' | 'deleting' | 'error' | 'success';

export function DeletePortalModal({ isOpen, portalName, boardName, portalId, boardId, onClose }: DeletePortalModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Phase flags make the delete resumable: a retry skips whichever step already succeeded
  // (re-calling the Cloudflare delete after it succeeded would 404).
  const [cloudflareDone, setCloudflareDone] = useState(false);
  const [storageDone, setStorageDone] = useState(false);

  const isDeleting = status === 'deleting';

  const handleDelete = async () => {
    setStatus('deleting');
    setErrorMessage(null);

    try {
      // Storage first: monday Storage and Cloudflare/D1 are independent stores, and the
      // registry rows are what keep the portal in the admin list. Deleting Storage before
      // the registry means a Storage failure leaves the portal listed and this modal's retry
      // can resume, rather than orphaning Storage under a portal that has already vanished.
      if (boardId && !storageDone) {
        await deletePortalStorage(Number(boardId));
        setStorageDone(true);
      }

      if (!cloudflareDone) {
        await deletePortalByPortalId(portalId);
        setCloudflareDone(true);
      }

      setStatus('success');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setStatus('error');
    }
  };

  const handleClose = () => {
    if (isDeleting) {
      return; // don't allow closing mid-deletion
    }
    // Once the Cloudflare/D1 rows are gone the portal drops out of the account list, so signal a refetch.
    onClose(cloudflareDone);
  };

  return (
    <AppModal show={isOpen} onClose={handleClose} contentSpacing={false} alertDialog={false} width="440px">
      <AppModalContent>
        {status === 'success' ? (
          <Box marginBottom="large">
            <Flex gap={8} align="center" style={{ marginBottom: '12px' }}>
              <Icon icon={Completed} size={22} />
              <Heading type="h3" weight="bold" align="start">
                Portal deleted
              </Heading>
            </Flex>
            <Text type="text2" weight="normal" align="start" color="primary" ellipsis={false}>
              All data for this portal has been permanently deleted.
            </Text>
          </Box>
        ) : (
          <>
            <Box marginBottom="medium">
              <Flex gap={8} align="center" style={{ marginBottom: '12px' }}>
                <Icon icon={Delete} size={22} />
                <Heading type="h3" weight="bold" align="start">
                  Delete portal
                </Heading>
              </Flex>
              <Text type="text2" weight="normal" align="start" color="primary" ellipsis={false}>
                This permanently deletes all data stored for this portal, including its settings, login methods and the connected custom
                domain. It does not delete the Clients Board or app views if still present in your workspace. This action cannot be undone
                and the data cannot be restored.
              </Text>
            </Box>

            <Box padding="medium" rounded="small" borderColor="layoutBorderColor" border>
              <Flex direction="column" gap={4} align="start">
                <Text type="text2" weight="medium" align="start" color="secondary" ellipsis={false}>
                  Portal Name → {portalName || 'Client Portal'}
                </Text>
                <Text type="text2" weight="medium" align="start" color="secondary" ellipsis={false}>
                  Clients Board → {boardName || 'Board not found'}
                </Text>
              </Flex>
            </Box>

            <Box marginTop="medium" marginBottom="medium">
              <Checkbox
                label="I agree to permanently delete all data for this portal."
                checked={confirmed}
                onChange={(e) => setConfirmed((e.target as HTMLInputElement).checked)}
                disabled={isDeleting}
              />
            </Box>

            {status === 'error' && errorMessage ? (
              <Box marginBottom="medium">
                <Flex gap={6} align="center" style={{ color: '#d83a52' }}>
                  <Icon icon={Alert} size={20} />
                  <div style={{ fontSize: '14px' }}>{errorMessage} You can try again to delete any remaining data.</div>
                </Flex>
              </Box>
            ) : null}

            <Box marginTop="medium" marginBottom="small">
              <Button
                size="medium"
                kind="primary"
                color="negative"
                loading={isDeleting}
                disabled={!confirmed || isDeleting}
                style={{ width: '100%' }}
                onClick={() => {
                  handleDelete();
                }}
              >
                Delete portal
              </Button>
            </Box>
          </>
        )}
      </AppModalContent>
    </AppModal>
  );
}
